import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui';
import { Loader2, X } from 'lucide-react';
import type * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCurrencies } from '@/features/catalog/api/currencies';
import { useProducts } from '@/features/catalog/api/products';
import type { Product } from '@/features/catalog/types/product.types';
import { useClients } from '@/features/crm/api/clients';
import { useProviders } from '@/features/crm/api/providers';
import { type Client, clientDisplayLabel } from '@/features/crm/types/client.types';
import type { Provider } from '@/features/crm/types/provider.types';
import { type LookupValueDto, useLookupValues } from '@/features/finance/api/lookup-values';
import { useWarehouses } from '@/features/inventory/api/warehouses';
import type { Warehouse } from '@/features/inventory/types/warehouse.types';
import { useUsers } from '@/features/users/api/users';
import type { UserWithRole } from '@/features/users/types/user.types';

/**
 * Reusable sales-picker primitives.
 *
 * Two families:
 *
 *   - Autocomplete pickers (`ClientPicker`, `ProductPicker`) — for
 *     collections too large to render in a static dropdown. They
 *     debounce a text query and call the existing list endpoints
 *     (`/clients`, `/products`). The wire payload still receives the
 *     UUID; the input shows the resolved label.
 *
 *   - Static dropdowns (`SellerSelect`, `CurrencySelect`,
 *     `SaleTypeSelect`) — for collections bounded by business
 *     rules (≈10 SELLERs per tenant, USD + VES today, a handful of
 *     sale-types in the lookup table).
 *
 * All pickers share the same `value` + `displayValue` contract so the
 * parent form can keep the UUID in form state without leaking it
 * into the field. `displayValue` falls back to the resolved record
 * (via `useClient` / `useProduct` in the parent) when the parent
 * received a pre-filled id without a name (CalculatorModal flow).
 */

// ---------------------------------------------------------------------------
// Autocomplete shell — keyboard-navigable listbox.
// ---------------------------------------------------------------------------

export interface SearchablePickerProps<T> {
  id: string;
  /** Current selection UUID; empty string when nothing is picked. */
  value: string;
  /** Resolved label to render when a value is set. */
  displayValue: string;
  onChange: (item: T) => void;
  onClear: () => void;
  placeholder: string;
  /** Free-text search the picker debounces internally. */
  query: string;
  onQueryChange: (next: string) => void;
  /** Results matching the debounced query. */
  results: readonly T[];
  isLoading: boolean;
  /** Stable unique key for React. */
  getKey: (item: T) => string;
  /** Primary label rendered in the dropdown row. */
  getLabel: (item: T) => string;
  /** Secondary hint (subtitle) — right-aligned, smaller. */
  getHint?: (item: T) => string;
  emptyMessage: string;
}

export function SearchablePicker<T>({
  id,
  value,
  displayValue,
  onChange,
  onClear,
  placeholder,
  query,
  onQueryChange,
  results,
  isLoading,
  getKey,
  getLabel,
  getHint,
  emptyMessage,
}: SearchablePickerProps<T>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Show the resolved label when a value is set; otherwise the live
  // search input. Once the user types, we treat it as a fresh search.
  const isSelected = Boolean(value) && query === '' && displayValue !== '';
  const inputValue = isSelected ? displayValue : query;

  // Close on outside click.
  useEffect(() => {
    function onDocClick(ev: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(ev.target as Node)) return;
      setOpen(false);
      // Snap the input back to the resolved label when blurring with
      // an empty selection (so the UUID never leaks into the field).
      if (!value) onQueryChange('');
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [value, onQueryChange]);

  const showResults = open && (isLoading || results.length > 0 || query.trim().length > 0);

  const pick = (item: T) => {
    onChange(item);
    onQueryChange('');
    setOpen(false);
  };

  const clear = () => {
    onClear();
    onQueryChange('');
    setOpen(true);
  };

  const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      if (!showResults) setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(results.length - 1, 0)));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (ev.key === 'Enter') {
      if (showResults && results[highlight]) {
        ev.preventDefault();
        pick(results[highlight]);
      }
    } else if (ev.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          id={id}
          name={id}
          autoComplete="off"
          role="combobox"
          aria-expanded={showResults}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          aria-activedescendant={
            results[highlight] ? `${id}-opt-${getKey(results[highlight])}` : undefined
          }
          value={inputValue}
          onFocus={() => {
            if (isSelected) onQueryChange('');
            setOpen(true);
          }}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={isSelected ? 'pr-8' : undefined}
        />
        {isSelected ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Limpiar selección"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {showResults ? (
        // The dropdown panel is wider than the input (min-w-[20rem]) so
        // long labels like "Hospital Universitario de Caracas" stay
        // readable even when the input itself sits inside a narrow
        // column (the QuotationForm uses md:grid-cols-3). The label and
        // hint stack vertically so the NAME — the primary identifier
        // the user is searching for — takes the top row, with the
        // identification number as a muted subtitle.
        <ul
          id={`${id}-listbox`}
          className="absolute left-0 z-50 mt-1 max-h-64 min-w-[20rem] max-w-[min(28rem,calc(100vw-2rem))] overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {isLoading ? (
            <li className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Buscando…
            </li>
          ) : null}
          {!isLoading && results.length === 0 ? (
            <li className="px-2 py-2 text-sm text-muted-foreground">{emptyMessage}</li>
          ) : null}
          {!isLoading &&
            results.map((item, i) => {
              const key = getKey(item);
              const label = getLabel(item);
              const hint = getHint?.(item);
              const active = i === highlight;
              return (
                <li
                  key={key}
                  id={`${id}-opt-${key}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    pick(item);
                  }}
                  className={`flex cursor-pointer flex-col gap-0.5 rounded-sm px-2 py-1.5 text-sm outline-none ${
                    active ? 'bg-accent text-accent-foreground' : ''
                  }`}
                >
                  <span className="truncate font-medium">{label}</span>
                  {hint ? (
                    <span className="truncate font-mono text-xs text-muted-foreground">{hint}</span>
                  ) : null}
                </li>
              );
            })}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Autocomplete pickers — back the shell with `/clients` / `/products`.
// ---------------------------------------------------------------------------

export interface ClientPickerProps {
  id: string;
  value: string;
  displayValue: string;
  onChange: (client: Client) => void;
  onClear: () => void;
  placeholder?: string;
}

export function ClientPicker({
  id,
  value,
  displayValue,
  onChange,
  onClear,
  placeholder = 'Buscar cliente por nombre…',
}: ClientPickerProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 250);
  const clients = useClients({
    page: 1,
    pageSize: 10,
    sort: 'name',
    ...(debounced ? { search: debounced } : {}),
  });

  return (
    <SearchablePicker<Client>
      id={id}
      value={value}
      displayValue={displayValue}
      onChange={onChange}
      onClear={onClear}
      placeholder={placeholder}
      query={query}
      onQueryChange={setQuery}
      results={clients.data?.data ?? []}
      isLoading={clients.isFetching}
      getKey={(c) => c.id}
      // Robust primary label — see `clientDisplayLabel` in
      // `@/features/crm/types/client.types`. Falls back to the
      // identification number when the stored name is empty / equals
      // the type / equals the number, the canonical placeholder
      // signatures that show up in seeds and ad-hoc admin creates.
      getLabel={clientDisplayLabel}
      getHint={(c) =>
        // Drop the redundant type prefix when the primary label is
        // already the identification number — otherwise the row
        // reads "J-20000008-7  RIF J-20000008-7" which is just noise.
        clientDisplayLabel(c) === c.identificationNumber
          ? c.identificationType
          : `${c.identificationType} ${c.identificationNumber}`
      }
      emptyMessage="No se encontraron clientes."
    />
  );
}

export interface ProviderPickerProps {
  id: string;
  value: string;
  displayValue: string;
  onChange: (provider: Provider) => void;
  onClear: () => void;
  placeholder?: string;
}

export function ProviderPicker({
  id,
  value,
  displayValue,
  onChange,
  onClear,
  placeholder = 'Buscar proveedor por nombre…',
}: ProviderPickerProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 250);
  const providers = useProviders({
    page: 1,
    pageSize: 10,
    sort: 'name',
    ...(debounced ? { search: debounced } : {}),
  });

  return (
    <SearchablePicker<Provider>
      id={id}
      value={value}
      displayValue={displayValue}
      onChange={onChange}
      onClear={onClear}
      placeholder={placeholder}
      query={query}
      onQueryChange={setQuery}
      results={providers.data?.data ?? []}
      isLoading={providers.isFetching}
      getKey={(p) => p.id}
      getLabel={(p) => p.name}
      getHint={(p) => p.identificationNumber}
      emptyMessage="No se encontraron proveedores."
    />
  );
}

export interface WarehousePickerProps {
  id: string;
  value: string;
  displayValue: string;
  onChange: (warehouse: Warehouse) => void;
  onClear: () => void;
  placeholder?: string;
}

export function WarehousePicker({
  id,
  value,
  displayValue,
  onChange,
  onClear,
  placeholder = 'Buscar depósito por nombre…',
}: WarehousePickerProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 250);
  const warehouses = useWarehouses({
    page: 1,
    pageSize: 10,
    sort: 'name',
    ...(debounced ? { search: debounced } : {}),
  });

  return (
    <SearchablePicker<Warehouse>
      id={id}
      value={value}
      displayValue={displayValue}
      onChange={onChange}
      onClear={onClear}
      placeholder={placeholder}
      query={query}
      onQueryChange={setQuery}
      results={warehouses.data?.data ?? []}
      isLoading={warehouses.isFetching}
      getKey={(w) => w.id}
      getLabel={(w) => `${w.code} · ${w.name}`}
      emptyMessage="No se encontraron depósitos."
    />
  );
}

export interface ProductPickerProps {
  id: string;
  value: string;
  displayValue: string;
  onChange: (product: Product) => void;
  onClear: () => void;
  placeholder?: string;
}

export function ProductPicker({
  id,
  value,
  displayValue,
  onChange,
  onClear,
  placeholder = 'Buscar producto por nombre o SKU…',
}: ProductPickerProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 250);
  const products = useProducts({
    page: 1,
    pageSize: 10,
    sort: 'name',
    ...(debounced ? { search: debounced } : {}),
  });

  return (
    <SearchablePicker<Product>
      id={id}
      value={value}
      displayValue={displayValue}
      onChange={onChange}
      onClear={onClear}
      placeholder={placeholder}
      query={query}
      onQueryChange={setQuery}
      results={products.data?.data ?? []}
      isLoading={products.isFetching}
      getKey={(p) => p.id}
      getLabel={(p) => p.name}
      getHint={(p) => p.sku}
      emptyMessage="No se encontraron productos."
    />
  );
}

// ---------------------------------------------------------------------------
// Static dropdowns — bounded collections (SELLERs, currencies,
// sale-types). They render with the project's `Select` primitive.
// ---------------------------------------------------------------------------

export interface SellerSelectProps {
  id: string;
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /**
   * When true, also surface ADMIN-role users as selectable vendors.
   * The BE `/users` endpoint accepts a single `role` filter, so we
   * issue a parallel query with `excludeRole='WAREHOUSE'` and merge
   * the results client-side (deduped by id). The default (false)
   * preserves the original SELLER-only behaviour and avoids a
   * second network round-trip when not needed.
   */
  includeAdmin?: boolean;
}

export function SellerSelect({
  id,
  value,
  onChange,
  placeholder = 'Elegí un vendedor',
  includeAdmin = false,
}: SellerSelectProps): React.ReactElement {
  const sellers = useUsers({ page: 1, pageSize: 100, role: 'SELLER' });
  // Hooks-rules compliance: always call useUsers the same number of
  // times. When `includeAdmin` is false we still call with a stable
  // (and disabled) query key so the hook order never changes across
  // renders.
  const admins = useUsers(
    includeAdmin ? { page: 1, pageSize: 100, excludeRole: 'WAREHOUSE' } : { page: 0, pageSize: 0 },
  );
  const list: UserWithRole[] = useMemo(() => {
    const seen = new Set<string>();
    const merged: UserWithRole[] = [];
    for (const u of sellers.data?.data ?? []) {
      if (seen.has(u.id)) continue;
      seen.add(u.id);
      merged.push(u);
    }
    if (includeAdmin) {
      for (const u of admins.data?.data ?? []) {
        if (seen.has(u.id)) continue;
        // Skip WAREHOUSE-only entries defensively — the BE filter is
        // the source of truth, but the merge runs before the BE
        // narrows the result.
        seen.add(u.id);
        merged.push(u);
      }
    }
    return merged;
  }, [sellers.data, admins.data, includeAdmin]);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {list.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.fullName} — {u.email}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface CurrencySelectProps {
  id: string;
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /**
   * If provided, only currencies whose `code` is in this list are
   * rendered. Use this when a domain object (e.g. bank accounts,
   * recurring expenses) is restricted to a specific subset of
   * currencies. Omit to show every active currency.
   */
  codes?: readonly string[];
}

export function CurrencySelect({
  id,
  value,
  onChange,
  placeholder = 'Elegí una moneda',
  codes,
}: CurrencySelectProps): React.ReactElement {
  const currencies = useCurrencies({
    page: 1,
    pageSize: 100,
    sort: 'code',
    dir: 'asc',
    isActive: 'active',
  });
  const list = (currencies.data?.data ?? []).filter((c) =>
    codes && codes.length > 0 ? codes.includes(c.code) : true,
  );

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {list.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.code} — {c.name} ({c.symbol})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface SaleTypeSelectProps {
  id: string;
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
}

/**
 * Sale-type dropdown. Sale types live in the GLOBAL
 * `lookup_values` table under `category='sale_type'` (sdd/finanzas-
 * modulo T2.10). We render `label` (`code` becomes the secondary
 * hint) sorted server-side by `sort_order`.
 */
export function SaleTypeSelect({
  id,
  value,
  onChange,
  placeholder = 'Elegí un tipo de venta',
}: SaleTypeSelectProps): React.ReactElement {
  const lookup = useLookupValues('sale_type', true);
  const list: LookupValueDto[] = lookup.data?.items ?? [];

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {list.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.label}
            {s.code ? ` — ${s.code}` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Shared `Label` shell so consumers do not need to import it from
// `@repo/ui` only to pair with a picker — purely ergonomic.
// ---------------------------------------------------------------------------

export function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: React.ReactNode;
}): React.ReactElement {
  return <Label htmlFor={htmlFor}>{children}</Label>;
}

// ---------------------------------------------------------------------------
// Local utilities (kept here because both forms reuse them).
// ---------------------------------------------------------------------------

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
